import { jsx as _jsx } from "react/jsx-runtime";
import React from 'react';
import { withTranslation } from 'react-i18next';
import { withRouter } from 'react-router';
import { format } from 'date-fns';
import { IS_APP_CODEAC } from '../../constants/env';
import filterProps from '../../utils/filterProps';
import { withHandleActivateTrial } from '../hoc/WithHandleActivateTrial';
import { withPlanInfo } from '../hoc/WithPlanInfo';
import { TrialGraph } from './TrialGraph';
import { BaseLineChart, generateTicks } from '../../pages/RepositoryPage/components/BaseLineChart';
export const GRAPH_VIEW_TYPE = {
    DASHBOARD: 'dashboard',
    COMMITS: 'commits',
};
export function getThresholdData(issuesThresholds) {
    const approxTotal = issuesThresholds.find(Boolean);
    const thresholdsValues = issuesThresholds.map((issue) => (issue !== null ? issue : undefined));
    const thresholdsProjectedValues = issuesThresholds.map((issue, index) => issue === null ? approxTotal : thresholdsValues[index]);
    return {
        thresholdsValues,
        thresholdsProjectedValues,
    };
}
function getDataForChart(commitList) {
    const dateTimes = [];
    const warnings = [];
    const errors = [];
    let maxIssues = 0;
    let issuesThresholds = [];
    let errorsThresholds = [];
    let commits = [];
    if (commitList?.commits.length) {
        const { commits: repositoryCommits, currentPage, limit } = commitList;
        commits = repositoryCommits.slice(limit * (currentPage - 1), limit * currentPage);
        Object.keys(commits).forEach((ind) => {
            const index = parseInt(ind);
            dateTimes.push(format(new Date(Number(commits[index].datetime)), 'MM/dd HH:mm'));
            warnings.push(commits[index].warningCount || 0);
            errors.push(commits[index].errorCount || 0);
            if (commits[index].errorCount + commits[index].warningCount > maxIssues) {
                maxIssues = commits[index].errorCount + commits[index].warningCount;
            }
        });
        const thresholds = commits.filter((commit) => commit.options && Object.prototype.hasOwnProperty.call(commit.options, 'thresholds'));
        if (thresholds.length) {
            const hasThresholdOptions = (index, extraOption) => {
                const commitOptions = commits[parseInt(index)].options;
                const basicCondition = commitOptions && Object.prototype.hasOwnProperty.call(commitOptions, 'thresholds');
                if (commitOptions && extraOption) {
                    const hasExtraOption = Object.prototype.hasOwnProperty.call(commitOptions.thresholds, extraOption);
                    return basicCondition && hasExtraOption;
                }
                return basicCondition;
            };
            issuesThresholds = Object.keys(commits).map((index) => hasThresholdOptions(index, 'total')
                ? commits[parseInt(index)].options.thresholds.total
                : null);
            errorsThresholds = Object.keys(commits).map((index) => hasThresholdOptions(index, 'error')
                ? commits[parseInt(index)].options.thresholds.error
                : null);
        }
    }
    let data = {
        errors,
        warnings,
        issuesThreshold: [],
        errorsThreshold: [],
        approxIssuesThreshold: [],
        approxErrorsThreshold: [],
    };
    if (issuesThresholds.length > 0) {
        const { thresholdsValues, thresholdsProjectedValues } = getThresholdData(issuesThresholds);
        data = {
            ...data,
            issuesThreshold: thresholdsValues,
            approxIssuesThreshold: thresholdsProjectedValues,
        };
    }
    if (errorsThresholds.length > 0) {
        const { thresholdsValues, thresholdsProjectedValues } = getThresholdData(errorsThresholds);
        data = {
            ...data,
            errorsThreshold: thresholdsValues,
            approxErrorsThreshold: thresholdsProjectedValues,
        };
    }
    return {
        ...data,
        maxIssues,
    };
}
export const LineChart = (props) => {
    const { trialGraphType = 'trend-in-time', isRepositoryPrivate, repositoryData, commitList, height, history, proPlan, t, ...rest } = filterProps(props, [
        'i18n',
        'handleActivateTrial',
        'lng',
        'location',
        'match',
        'staticContext',
        'tReady',
        'trialActivateable',
        'user',
    ]);
    if (!commitList.commits.length) {
        return _jsx("p", { className: 'pt-3', children: t('noChartData') });
    }
    const dataGraph = getDataForChart(commitList);
    const { commits, limit, currentPage } = commitList;
    const slicedCommits = commits.slice(limit * (currentPage - 1), limit * currentPage);
    const showTrialGraph = IS_APP_CODEAC && !proPlan && isRepositoryPrivate;
    const handleClick = (chartElement) => {
        const commitHash = slicedCommits.reverse()[chartElement.activeTooltipIndex].hash;
        if (chartElement.activeLabel && commitHash) {
            history.push(`${repositoryData.getBaseLink()}/commits/${commitHash}`);
        }
    };
    const calculateMaxIssues = (maxIssues, issuesThreshold, errorsThreshold) => {
        if (issuesThreshold || errorsThreshold) {
            const thresholdMax = issuesThreshold < errorsThreshold ? errorsThreshold : issuesThreshold;
            return maxIssues > thresholdMax ? maxIssues : thresholdMax;
        }
        return maxIssues;
    };
    let maxIssues = 0;
    const data = slicedCommits.map((commit, index) => {
        const calculatedMaxIssues = calculateMaxIssues(dataGraph.maxIssues, dataGraph.issuesThreshold[index], dataGraph.errorsThreshold[index]);
        if (maxIssues < calculatedMaxIssues) {
            maxIssues = calculatedMaxIssues;
        }
        return {
            dateTime: format(new Date(Number(commit.datetime)), 'MM/dd HH:mm'),
            message: commit.message,
            hash: commit.hash,
            id: commit.id,
            chartLines: {
                issuesThreshold: dataGraph.issuesThreshold[index],
                approxIssuesThreshold: dataGraph.approxIssuesThreshold[index],
                errorsThreshold: dataGraph.errorsThreshold[index],
                approxErrorsThreshold: dataGraph.approxErrorsThreshold[index],
                errors: dataGraph.errors[index] || 0,
                warnings: dataGraph.errors[index] + dataGraph.warnings[index] || 0,
            },
            maxIssues,
        };
    }).reverse();
    const chartStep = maxIssues > 1000 ? 100 : maxIssues < 100 ? 5 : 10;
    const step = maxIssues / chartStep > 10 ? chartStep * 2 : chartStep;
    return (_jsx("div", { ...rest, children: showTrialGraph ? (_jsx(TrialGraph, { trialGraphType: trialGraphType, handleActivateTrial: props.handleActivateTrial })) :
            _jsx(BaseLineChart, { height: height, chartData: data, yTicks: generateTicks(0, maxIssues + chartStep, step), chartType: 'trendInTime', tooltipData: commits, handleClick: handleClick, shortDate: true }) }));
};
// @ts-expect-error TS2345: Argument of type is not assignable to parameter of type
export default withRouter(withTranslation()(withHandleActivateTrial(withPlanInfo(LineChart))));
